import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { AuthLayout } from '../../components/AuthLayout';
import { Button } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import { Alert, AlertDescription } from '@werkstatt/ui';
import { Checkbox } from '@werkstatt/ui';
import { AlertCircle } from 'lucide-react';
import { logger } from '@/utils/logger';
import { readApiErrorCode } from '../../lib/apiError';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoading, error } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [localError, setLocalError] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    try {
      await login({
        email, password, remember_me: rememberMe,
        totp_code: needsTotp ? totpCode : undefined,
      });
      const loggedInUser = useAuthStore.getState().user;
      const next = searchParams.get('next') ||
        (loggedInUser?.role === 'super_admin' ? '/superadmin' : '/dashboard');
      navigate(next, { replace: true });
    } catch (err: unknown) {
      if (readApiErrorCode(err) === 'totp_required') {
        setNeedsTotp(true);
      } else {
        logger.error('Login error:', err);
      }
    }
  };

  const displayError = error || localError;

  return (
    <AuthLayout
      title="Anmelden"
      subtitle="Geben Sie Ihre Zugangsdaten ein"
      footer={
        <p>
          Noch kein Konto?{' '}
          <a href="https://werkstatt-connect.de" className="text-primary hover:text-primary/80 font-medium">
            Zeiterfassung abonnieren
          </a>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {displayError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{displayError}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              type="email"
              placeholder="ihre.email@beispiel.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              autoComplete="email"
              className="mt-1.5"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label htmlFor="password">Passwort</Label>
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-primary hover:text-primary/80"
              >
                Passwort vergessen?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              autoComplete="current-password"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="rememberMe"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(checked === true)}
              disabled={isLoading}
            />
            <Label htmlFor="rememberMe" className="text-sm font-normal cursor-pointer">
              Angemeldet bleiben (30 Tage)
            </Label>
          </div>

          {needsTotp && (
            <div>
              <Label htmlFor="totp-code">2FA-Code</Label>
              <Input
                id="totp-code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="123456 oder Backup-Code"
                disabled={isLoading}
                required
                autoFocus
                className="mt-1.5"
              />
            </div>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Anmeldung läuft...' : needsTotp ? 'Bestätigen' : 'Anmelden'}
        </Button>
      </form>
    </AuthLayout>
  );
}
